import { AO_DEFAULT_PARAMETERS } from './ambient-occlusion-pass';

/**
 * World AO radius (m) per metre of camera distance for the light model in MODEL navigation.
 * GTAO's radius is a world distance (AO_DEFAULT_PARAMETERS.screenSpaceRadius is false): the
 * fixed 1 m suits REGIONS/SECTIONS interiors, but at whole-ship view (camera several hundred
 * metres out) 1 m is about 1 px on screen and the AO vanishes. Scaling the radius with the
 * distance keeps it a roughly constant fraction of the view (0.05 ≈ 25 m at 500 m). Tuned in
 * the browser through Graphics.aoLightModelRadiusPerMetre.
 */
export const AO_LIGHT_MODEL_RADIUS_PER_METRE = 0.05;
// was: export const AO_LIGHT_MODEL_RADIUS_PER_METRE = 0.02; — too faint at whole-ship view (UAT 2026-10-30)

/**
 * GTAO `scale` uniform for the light model in MODEL navigation (the shader does
 * `ao = pow(ao, scale)`, so above 1 darkens the occlusion; AO_DEFAULT_PARAMETERS.scale is 1).
 * Measured at UAT 2026-10-30 at whole-ship view; tuned live through Graphics.aoLightModelScale.
 */
// was: export const AO_LIGHT_MODEL_SCALE = 2; — a bit too dark once the crease edges were added (UAT 2026-10-30)
export const AO_LIGHT_MODEL_SCALE = 1.5;

export interface AmbientOcclusionRadiusInput {
  /** Asset.mode: 'MODEL' | 'REGIONS' | 'SECTIONS' */
  navigationMode: string | null | undefined;
  /** Asset.isLightModel */
  isLightModel: boolean;
  /** Camera distance (m) to the orbit target. */
  cameraDistance: number;
  /** World radius per metre of camera distance (Graphics.aoLightModelRadiusPerMetre). */
  radiusPerMetre: number;
  /** GTAO scale for the light model in MODEL navigation (Graphics.aoLightModelScale). */
  lightModelScale: number;
}

export interface AmbientOcclusionWorldRadius {
  /** gtaoMaterial.uniforms.radius (m) */
  radius: number;
  /** gtaoMaterial.uniforms.thickness (m); also the shader's depth-delta cut-off, so it follows the radius. */
  thickness: number;
  /** gtaoMaterial.uniforms.scale (exponent applied to the AO term). */
  scale: number;
}

/**
 * GTAO radius, thickness and scale for this frame. The light model in MODEL navigation gets
 * max(AO_DEFAULT_PARAMETERS.radius, radiusPerMetre × cameraDistance) for both radius and
 * thickness, and lightModelScale as the scale (AO_DEFAULT_PARAMETERS.scale if it is not
 * finite); everything else (REGIONS, SECTIONS, the full model in MODEL) and any non-finite
 * or negative distance / radius per metre keeps exactly AO_DEFAULT_PARAMETERS.radius /
 * .thickness / .scale.
 */
export function ambientOcclusionWorldRadius(input: AmbientOcclusionRadiusInput): AmbientOcclusionWorldRadius {
  const defaults = { radius: AO_DEFAULT_PARAMETERS.radius, thickness: AO_DEFAULT_PARAMETERS.thickness, scale: AO_DEFAULT_PARAMETERS.scale };
  if (input.navigationMode !== 'MODEL' || !input.isLightModel) {
    return defaults;
  }
  const { cameraDistance, radiusPerMetre, lightModelScale } = input;
  if (!Number.isFinite(cameraDistance) || cameraDistance < 0 || !Number.isFinite(radiusPerMetre)) {
    return defaults;
  }
  const radius = Math.max(AO_DEFAULT_PARAMETERS.radius, radiusPerMetre * cameraDistance);
  const scale = Number.isFinite(lightModelScale) ? lightModelScale : AO_DEFAULT_PARAMETERS.scale;
  return { radius, thickness: radius, scale };
}
